
'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { motion } from 'framer-motion';
import { Zap, Palette, Scaling, ArrowRight, CreditCard, Layout, ShoppingCart, BarChart, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from '@/navigation';

export default function ProductsPage() {
  const t = useTranslations('products');

  // Marketing Trio Metrics ma'lumotlari
  const metrics = [
    { 
      icon: Zap,
      title: "95+ Score", 
      text: "Biz yaratgan saytlar Google PageSpeed reytingida eng yuqori tezlikda ishlaydi, bu esa mijozlaringiz kutib qolmasligini ta'minlaydi."
    },
    { 
      icon: Palette,
      title: "Custom UX", 
      text: "Tayyor shablonlardan foydalanmaymiz. Har bir biznes uchun individual, bozorda ajralib turadigan premium dizayn chizamiz."
    },
    { 
      icon: Scaling,
      title: "Scalable Code", 
      text: "Zamonaviy Next.js va Supabase ekotizimi tufayli loyihangiz kelajakda millionlab foydalanuvchilarni qiyinchiliksiz ko'tara oladi."
    },
  ];

  const products = [
    {
      name: "HVN Studio",
      tagline: "No-code sayt yaratish platformasi",
      desc: "O'zbekistondagi birinchi no-code web builder. Kod bilmasdan professional sayt yarating — drag & drop bilan.",
      features: ["Drag & Drop", "100+ Shablon", "O'zbek tili", "Bepul boshlash"],
      type: "SaaS Platform",
      status: "Beta",
      icon: Zap,
      color: "from-[#667eea] to-[#764ba2]"
    },
    // ... (qolgan mahsulotlar shu yerda qoladi)
  ];

  return (
    <main className="min-h-screen bg-white dark:bg-black">
      <Navbar />
      
      {/* HERO SECTION WITH NEW MARKETING HEADLINE */}
      <section className="py-24 md:py-32 bg-zinc-50 dark:bg-zinc-950">
        <div className="container mx-auto px-6 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-5xl font-bold tracking-tight text-black dark:text-white mb-6 max-w-4xl mx-auto"
          >
            {t('headline')}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-base md:text-lg text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto"
          >
            {t('subheadline')}
          </motion.p>
        </div>
      </section>

      {/* MARKETING TRIO METRICS SECTION */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {metrics.map((metric, i) => {
              const Icon = metric.icon;
              return (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="bg-white dark:bg-zinc-900 p-6 rounded-2xl border border-zinc-100 dark:border-zinc-800"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <Icon className="w-6 h-6 text-black dark:text-white" />
                    <h3 className="text-xl font-bold text-black dark:text-white">{metric.title}</h3>
                  </div>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">
                    {metric.text}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* PRODUCTS GRID (EXISTING) */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {products.map((product, i) => {
                const Icon = product.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-white border border-gray-100 rounded-[2.5rem] p-10 group hover:border-black transition-all dark:bg-zinc-900 dark:border-zinc-800 dark:hover:border-white"
                  >
                    <div className="flex flex-col md:flex-row gap-8">
                      <div className={`w-20 h-20 shrink-0 bg-gradient-to-br ${product.color} rounded-2xl flex items-center justify-center text-white`}>
                        <Icon size={40} />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-2xl font-bold dark:text-white">{product.name}</h3>
                          <StatusBadge status={product.status} />
                        </div>
                        <p className="text-sm font-medium italic text-gray-400 mb-6">{product.tagline}</p>
                        <p className="text-gray-500 text-sm leading-relaxed mb-8 dark:text-gray-400">
                          {product.desc}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-10">
                          {product.features.map(f => (
                            <span key={f} className="text-[10px] font-bold uppercase tracking-wider px-3 py-1 bg-gray-50 rounded-full dark:bg-zinc-800 dark:text-gray-400">
                              {f}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center justify-between pt-8 border-t border-gray-50 dark:border-zinc-800">
                          <span className="text-xs font-mono text-gray-400 uppercase tracking-widest">{product.type}</span>
                          <Button className="rounded-full px-6 bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200">
                            {t('visit')} <ArrowRight size={16} className="ml-2" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
          </div>
        </div>
      </section>
      
      {/* CTA SECTION (Optional: Can be kept or removed) */}
      <section className="py-24 bg-black text-white text-center">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-6xl font-display italic mb-8">Yangi mahsulot g'oyangiz bormi?</h2>
          <p className="text-xl text-gray-400 mb-12">Birgalikda yarataylik</p>
          <Button asChild className="rounded-full h-14 px-10 bg-white text-black hover:bg-gray-200">
            <Link href="/#contact">Hamkorlik →</Link>
          </Button>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: any = {
    "Faol": "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400",
    "Beta": "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400",
    "Tez orada": "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400"
  };

  return (
    <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-0.5 rounded-full ${styles[status] || styles["Faol"]}`}>
      {status}
    </span>
  );
}
